<section class="tc-steps" aria-labelledby="tc-steps-heading">
  <to-section-title
    headingId="tc-steps-heading"
    [title]="'testCases.form.testSteps.blockTitle' | translate"
    [subtitle]="'testCases.form.testSteps.blockSubtitle' | translate: { count: steps().length, passed: passedCount() }"
  >
    <div right class="tc-steps__actions">
      <button
        mat-stroked-button
        type="button"
        class="tc-steps__library-btn"
        [disabled]="busy() || readOnly()"
        (click)="openLibrary()"
      >
        <mat-icon aria-hidden="true">folder</mat-icon>
        {{ 'testCases.form.testSteps.library' | translate }}
      </button>
      @if (allowModuleEmbed()) {
        <button
          mat-stroked-button
          type="button"
          class="tc-steps__embed-btn"
          [disabled]="busy() || readOnly()"
          (click)="openModuleEmbed()"
        >
          <mat-icon aria-hidden="true">extension</mat-icon>
          {{ 'testCases.form.testSteps.embedModule' | translate }}
        </button>
      }
      <button
        mat-flat-button
        color="primary"
        type="button"
        class="tc-steps__add-btn"
        [disabled]="busy() || readOnly() || draftStep() !== null"
        (click)="startDraft()"
      >
        {{ 'testCases.form.testSteps.addStep' | translate }}
      </button>
    </div>
  </to-section-title>

  <!-- Nur der Testfall-Editor projiziert hier seinen KI-Qualitätscheck, der Modul-Editor nicht. -->
  <ng-content select="[stepsCheck]" />

  <app-test-step-library-dialog
    [projectId]="projectId()"
    [visible]="showLibrary()"
    (visibleChange)="showLibrary.set($event)"
    (applied)="onLibraryApplied($event)"
  ></app-test-step-library-dialog>

  @if (allowModuleEmbed()) {
    <app-module-embed-dialog
      [projectId]="projectId()"
      [excludeIds]="moduleEmbedExcludeIds()"
      [visible]="showModuleEmbed()"
      (visibleChange)="showModuleEmbed.set($event)"
      (applied)="onModuleEmbedApplied($event)"
    ></app-module-embed-dialog>
  }

  @if (loading()) {
    <div class="tc-steps__loading">
      <mat-spinner diameter="28" />
    </div>
  } @else {
    @if (error()) {
      <p class="tc-steps__error" role="alert">{{ 'testCases.form.testSteps.error' | translate }}</p>
    }

    <button
      type="button"
      role="switch"
      class="tc-steps__tcdl-all"
      data-testid="tcdl-toggle-all"
      [class.tc-steps__tcdl-all--on]="tcdlAll()"
      [attr.aria-checked]="tcdlAll()"
      (click)="toggleTcdlAll()"
    >
      <span class="tc-steps__tcdl-all-track" aria-hidden="true">
        <span class="tc-steps__tcdl-all-thumb"></span>
      </span>
      <span class="tc-steps__tcdl-all-body">
        <span class="tc-steps__tcdl-all-title">{{ 'testSteps.form.tcdlToggleAll' | translate }}</span>
        <span class="tc-steps__tcdl-all-subline">{{ tcdlToggleAllSublineKey() | translate }}</span>
      </span>
    </button>

    <div
      class="tc-steps__table"
      appScrollContainer
      role="table"
      [attr.aria-label]="'testCases.form.testSteps.tableLabel' | translate"
    >
      <div class="tc-steps__thead" role="row">
        <div class="tc-steps__th tc-steps__th--main" role="columnheader">
          {{ 'testCases.form.testSteps.action' | translate }} /
          {{ 'testCases.form.testSteps.expectedResult' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--link" role="columnheader">
          {{ 'testCases.form.testSteps.linkColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--maintainer" role="columnheader">
          {{ 'testCases.form.testSteps.maintainerColumn' | translate }}
        </div>
        <div class="tc-steps__th tc-steps__th--actions" role="columnheader">
          {{ 'testCases.form.testSteps.actions' | translate }}
        </div>
      </div>

      @if (tableRows().length > 0) {
        @for (step of tableRows(); track step.id; let i = $index) {
          @if (isModuleRow(step)) {
            <div class="tc-steps__row tc-steps__module-row" [class.tc-steps__row--even]="i % 2 === 0" role="row">
              <span class="tc-steps__bubble" aria-hidden="true">{{ i + 1 }}</span>
              <button
                type="button"
                class="tc-steps__module-toggle"
                [attr.aria-expanded]="isModuleExpanded(step.id)"
                [attr.aria-label]="
                  (isModuleExpanded(step.id)
                    ? 'testCases.form.testSteps.moduleCollapse'
                    : 'testCases.form.testSteps.moduleExpand'
                  ) | translate
                "
                (click)="toggleModuleExpand(step)"
              >
                <mat-icon aria-hidden="true">
                  {{ isModuleExpanded(step.id) ? 'expand_more' : 'chevron_right' }}
                </mat-icon>
              </button>
              <div class="tc-steps__main tc-steps__module-main" role="cell">
                <div class="tc-steps__module-head">
                  <mat-icon class="tc-steps__module-icon" aria-hidden="true">extension</mat-icon>
                  <span class="tc-steps__module-name">{{ liveModuleTitle(step) }}</span>
                  @if (showStepKeys() && step.key; as key) {
                    <app-step-key-pill [key]="key" />
                  }
                  @if (liveModuleStepCount(step) !== null) {
                    <span class="tc-steps__module-badge">
                      {{ 'testCases.form.testSteps.moduleStepCount' | translate: { count: liveModuleStepCount(step) } }}
                    </span>
                  }
                  @if (step.calledModuleId) {
                    <button
                      type="button"
                      class="tc-steps__module-edit-btn"
                      data-testid="module-edit-open-drawer"
                      [attr.aria-label]="'testCases.form.testSteps.editModuleAria' | translate"
                      [disabled]="busy() || readOnly()"
                      (click)="onEditModule(step)"
                    >
                      <mat-icon aria-hidden="true">edit</mat-icon>
                    </button>
                  }
                  @for (sheet of linkedSheetsForStep(step); track sheet.id) {
                    <span
                      class="tc-steps__tdb-chip"
                      [class.tc-steps__tdb-chip--restricted]="isChipRestricted(step, sheet)"
                      [attr.data-testid]="'tdb-chip-' + sheet.key"
                      [attr.title]="chipTooltip(step, sheet)"
                    >
                      <mat-icon aria-hidden="true">table_chart</mat-icon>
                      {{ chipLabel(step, sheet) }}
                    </span>
                  }
                  @if (step.calledModuleId) {
                    <button
                      type="button"
                      class="tc-steps__module-open"
                      [attr.aria-label]="'testCases.form.testSteps.openModule' | translate"
                      (click)="openModule(step)"
                    >
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                    </button>
                  }
                </div>
                @if (tcdlAll() && !isModuleExpanded(step.id)) {
                  <code
                    class="tc-steps__condition-preview tc-steps__module-tcdl"
                    [attr.data-testid]="'module-tcdl-' + step.id"
                  >
                    {{ moduleTcdlPreview(step) }}
                  </code>
                }
                @if (isModuleExpanded(step.id)) {
                  <div class="tc-steps__module-steps">
                    @if (blockRows(step).length > 0) {
                      <div class="tc-steps__tdb-panel">
                        <app-testdaten-block
                          [moduleTitle]="moduleTitleFor(step)"
                          [rows]="blockRows(step)"
                          [disabled]="readOnly()"
                          [showPreview]="tcdlAll()"
                          (overrideChanged)="onBlockOverrideChanged(step, $event)"
                          (overrideReset)="onBlockOverrideReset(step)"
                        />
                      </div>
                    } @else {
                      <div class="tc-steps__condition-panel" [attr.data-testid]="'step-condition-block-' + step.id">
                        <div class="tc-steps__condition-label">{{ 'tdb.field.condition' | translate }}</div>
                        <app-step-condition-editor
                          [override]="currentOverride(step)"
                          [scopeId]="step.id"
                          [disabled]="readOnly()"
                          (changed)="onConditionChanged(step, $event)"
                        />
                        @if (tcdlAll()) {
                          <code
                            class="tc-steps__condition-preview"
                            [attr.data-testid]="'step-condition-preview-' + step.id"
                          >
                            {{ moduleTcdlPreview(step) }}
                          </code>
                        }
                      </div>
                    }
                    @if (moduleLoadFailed(step.id)) {
                      <p class="tc-steps__module-error" role="alert">
                        {{ 'testCases.form.testSteps.moduleLoadError' | translate }}
                        <button type="button" class="tc-steps__module-retry" (click)="retryModuleLoad(step)">
                          {{ 'testCases.form.testSteps.moduleLoadRetry' | translate }}
                        </button>
                      </p>
                    } @else {
                      <p class="tc-steps__module-readonly-hint">
                        {{ 'testCases.form.testSteps.moduleReadonlyHint' | translate }}
                      </p>
                      @for (inner of liveModuleSteps(step); track inner.id; let j = $index) {
                        @if (isModuleRow(inner) && inner.calledModuleId) {
                          <app-module-step-tree
                            class="tc-steps__module-tree"
                            [projectId]="projectId()"
                            [moduleId]="inner.calledModuleId"
                            [name]="liveModuleTitle(inner)"
                            [ancestors]="step.calledModuleId ? [step.calledModuleId] : []"
                            [prefix]="moduleStepNumber(i, j)"
                            [availableSheets]="availableSheets()"
                            [showStepKeys]="showStepKeys()"
                            [stepKey]="inner.key"
                            (editModule)="onNestedModuleEdit($event)"
                          />
                        } @else {
                          <div class="tc-steps__module-substep">
                            <span class="tc-steps__module-substep-nr">{{ moduleStepNumber(i, j) }}</span>
                            <div class="tc-steps__module-substep-body">
                              <div class="tc-steps__module-substep-title-row">
                                <div class="tc-steps__module-substep-title">
                                  {{ inner.title || inner.action || '—' }}
                                </div>
                                @if (showStepKeys() && inner.key; as key) {
                                  <app-step-key-pill [key]="key" />
                                }
                              </div>
                              @if (inner.expectedResult) {
                                <div class="tc-steps__module-substep-expected">{{ inner.expectedResult }}</div>
                              }
                            </div>
                          </div>
                        }
                      } @empty {
                        <p class="tc-steps__module-empty">{{ 'testCases.form.testSteps.moduleEmpty' | translate }}</p>
                      }
                    }
                  </div>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--actions" role="cell">
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveUp' | translate"
                  [disabled]="busy() || readOnly() || i === 0"
                  (click)="moveUp(i)"
                >
                  <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveDown' | translate"
                  [disabled]="busy() || readOnly() || i === steps().length - 1"
                  (click)="moveDown(i)"
                >
                  <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn tc-steps__action-btn--delete"
                  [attr.aria-label]="'testCases.form.testSteps.delete' | translate"
                  [disabled]="busy() || readOnly()"
                  (click)="delete(step)"
                >
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
              </div>
            </div>
          } @else if (editingId() === step.id) {
            <div class="tc-steps__row tc-steps__row--editing" role="row">
              <span class="tc-steps__bubble tc-steps__bubble--editing" aria-hidden="true">{{ i + 1 }}</span>
              <div class="tc-steps__edit">
                <input
                  #editTitleInput
                  type="text"
                  class="tc-steps__edit-title"
                  [value]="editTitle()"
                  [attr.maxlength]="fieldLengths.NAME_MAX"
                  (input)="onEditTitleInput($event)"
                  (keydown.enter)="saveEdit()"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.title' | translate"
                  [placeholder]="'testCases.form.testSteps.titlePlaceholder' | translate"
                  required
                  aria-required="true"
                  [attr.aria-invalid]="!editTitle().trim() ? 'true' : null"
                />
                @if (!editTitle().trim()) {
                  <span class="tc-steps__edit-required" role="alert">
                    {{ 'testCases.form.testSteps.titleRequired' | translate }}
                  </span>
                }
                <textarea
                  class="tc-steps__edit-action"
                  rows="2"
                  [value]="editAction()"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  (input)="onEditActionInput($event)"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.action' | translate"
                  [placeholder]="'testCases.form.testSteps.actionPlaceholder' | translate"
                ></textarea>
                <textarea
                  class="tc-steps__edit-expected"
                  rows="2"
                  [value]="editExpected()"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  (input)="onEditExpectedInput($event)"
                  (keydown.escape)="cancelEdit()"
                  [attr.aria-label]="'testCases.form.testSteps.expectedResult' | translate"
                  [placeholder]="'testCases.form.testSteps.expectedResultPlaceholder' | translate"
                  required
                  aria-required="true"
                  [attr.aria-invalid]="!editExpected().trim() ? 'true' : null"
                ></textarea>
                @if (!editExpected().trim()) {
                  <span class="tc-steps__edit-required" role="alert">
                    {{ 'testCases.form.testSteps.expectedResultRequired' | translate }}
                  </span>
                }
                @if (tcdlAll()) {
                  <span class="tc-steps__edit-tcdl-label">
                    {{ 'testSteps.form.tcdlLabel' | translate }}
                  </span>
                  <app-tcdl-editor
                    size="compact"
                    [projectId]="projectId()"
                    [value]="editTcdl()"
                    [freetext]="editAction()"
                    [maxLength]="fieldLengths.FREETEXT_MAX"
                    [ariaLabel]="'testSteps.form.tcdlEditorAriaLabel' | translate"
                    (valueChange)="onEditTcdlChange($event)"
                    (advanceRequested)="onTcdlAdvance()"
                  />
                }
                <div class="tc-steps__edit-controls">
                  <input
                    #editNotesInput
                    type="text"
                    class="tc-steps__edit-link"
                    [value]="editNotes()"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    (input)="onEditNotesInput($event)"
                    (keydown.enter)="saveEdit()"
                    (keydown.escape)="cancelEdit()"
                    [attr.aria-label]="'testCases.form.testSteps.linkColumn' | translate"
                    [placeholder]="'testCases.form.testSteps.linkPlaceholder' | translate"
                  />
                  <span class="tc-steps__edit-spacer"></span>
                  <button type="button" class="tc-steps__edit-btn tc-steps__edit-btn--cancel" (click)="cancelEdit()">
                    {{ 'testCases.form.testSteps.cancelEdit' | translate }}
                  </button>
                  <button
                    type="button"
                    class="tc-steps__edit-btn tc-steps__edit-btn--save"
                    [disabled]="busy() || !canSaveEdit()"
                    (click)="saveEdit()"
                  >
                    <mat-icon aria-hidden="true">check</mat-icon>
                    {{ 'testCases.form.testSteps.applyEdit' | translate }}
                  </button>
                </div>
              </div>
            </div>
          } @else {
            <div
              class="tc-steps__row"
              [class.tc-steps__row--even]="i % 2 === 0"
              [attr.data-step-id]="step.id"
              role="row"
            >
              <span class="tc-steps__bubble" aria-hidden="true">{{ i + 1 }}</span>
              <div class="tc-steps__main" role="cell">
                <div class="tc-steps__title-row">
                  <div class="tc-steps__title">{{ step.title || step.action || '—' }}</div>
                  @if (showStepKeys() && step.key; as key) {
                    <app-step-key-pill [key]="key" />
                  }
                </div>
                @if (step.expectedResult) {
                  <div class="tc-steps__desc">{{ step.expectedResult }}</div>
                }
                @if (kiBefundSet().has(step.id) || kiGeaendertIds().has(step.id)) {
                  <div class="tc-steps__ki-marker" data-testid="ki-schritt-marker">
                    @if (kiBefundSet().has(step.id)) {
                      <app-ki-marker art="befund" [hervorgehoben]="kiHervorgehobenId() === step.id" />
                    }
                    @if (kiGeaendertIds().has(step.id)) {
                      <app-ki-marker art="geaendert" />
                    }
                  </div>
                }
                @if (tcdlAll() && hasTcdl(step)) {
                  <div class="tc-steps__tcdl-block" [attr.data-testid]="'tcdl-row-collapse-' + step.id">
                    <button
                      type="button"
                      class="tc-steps__tcdl-toggle"
                      [class.tc-steps__tcdl-toggle--static]="!tcdlIsLong(step)"
                      [attr.aria-expanded]="isTcdlExpanded(step.id)"
                      [disabled]="!tcdlIsLong(step)"
                      (click)="toggleTcdl(step.id)"
                    >
                      <span class="tc-steps__tcdl-toggle-label">
                        @if (tcdlManualCount(step) > 0) {
                          {{
                            'testSteps.form.tcdlCollapseWithManual'
                              | translate: { count: tcdlInstructionCount(step), manual: tcdlManualCount(step) }
                          }}
                        } @else {
                          {{ 'testSteps.form.tcdlCollapse' | translate: { count: tcdlInstructionCount(step) } }}
                        }
                      </span>
                      @if (tcdlIsLong(step)) {
                        <mat-icon aria-hidden="true">
                          {{ isTcdlExpanded(step.id) ? 'expand_more' : 'chevron_right' }}
                        </mat-icon>
                      }
                    </button>
                    <div class="tc-steps__tcdl-list">
                      @for (line of tcdlVisibleLines(step); track $index) {
                        <div class="tc-steps__tcdl-line-row">
                          <span class="tc-steps__tcdl-line-num">{{ line.trim() ? $index + 1 : 'M' }}</span>
                          <code
                            class="tc-steps__tcdl-line-text"
                            [class.tc-steps__tcdl-line-text--manual]="!line.trim()"
                          >
                            {{ line.trim() || ('testSteps.form.tcdlManualLine' | translate) }}
                          </code>
                        </div>
                      }
                      @if (tcdlIsLong(step) && !isTcdlExpanded(step.id)) {
                        <button type="button" class="tc-steps__tcdl-more" (click)="toggleTcdl(step.id)">
                          {{ 'testSteps.form.tcdlMoreLines' | translate: { count: tcdlHiddenLineCount(step) } }}
                        </button>
                      }
                    </div>
                  </div>
                } @else if (tcdlAll()) {
                  <div class="tc-steps__tcdl-empty" [attr.data-testid]="'tcdl-empty-' + step.id">
                    <code class="tc-steps__tcdl-line-text tc-steps__tcdl-line-text--manual">
                      {{ 'testSteps.form.tcdlEmptyLine' | translate }}
                    </code>
                    @if (!readOnly()) {
                      <span class="tc-steps__tcdl-empty-sep" aria-hidden="true">·</span>
                      <button
                        type="button"
                        class="tc-steps__tcdl-more"
                        [attr.data-testid]="'tcdl-add-' + step.id"
                        [disabled]="busy()"
                        (click)="startEditTcdl(step)"
                      >
                        {{ 'testSteps.form.tcdlAddLink' | translate }}
                      </button>
                    }
                  </div>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--link" role="cell">
                @if (step.notes) {
                  {{ step.notes }}
                } @else {
                  <span class="tc-steps__faint">—</span>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--maintainer" role="cell">
                @if (currentUserName()) {
                  <app-avatar [name]="currentUserName()" [size]="24" />
                  <span class="tc-steps__maintainer-name">{{ currentUserName() }}</span>
                } @else {
                  <span class="tc-steps__faint">—</span>
                }
              </div>
              <div class="tc-steps__col tc-steps__col--actions" role="cell">
                <button
                  type="button"
                  class="tc-steps__action-btn tc-steps__action-btn--edit"
                  [attr.aria-label]="'testCases.form.testSteps.edit' | translate"
                  [disabled]="busy() || readOnly()"
                  (click)="startEdit(step)"
                >
                  <mat-icon aria-hidden="true">edit</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveUp' | translate"
                  [disabled]="busy() || readOnly() || i === 0 || isDraft(step)"
                  (click)="moveUp(i)"
                >
                  <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn"
                  [attr.aria-label]="'testCases.form.testSteps.moveDown' | translate"
                  [disabled]="busy() || readOnly() || i === steps().length - 1 || isDraft(step)"
                  (click)="moveDown(i)"
                >
                  <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                </button>
                <button
                  type="button"
                  class="tc-steps__action-btn tc-steps__action-btn--delete"
                  [attr.aria-label]="'testCases.form.testSteps.delete' | translate"
                  [disabled]="busy() || readOnly()"
                  (click)="delete(step)"
                >
                  <mat-icon aria-hidden="true">delete</mat-icon>
                </button>
              </div>
            </div>
          }
        }
      } @else {
        <div class="tc-steps__empty">
          <p class="tc-steps__empty-title">{{ 'testCases.form.testSteps.emptyState.title' | translate }}</p>
          <p class="tc-steps__empty-hint">{{ 'testCases.form.testSteps.emptyState.hint' | translate }}</p>
          <button
            mat-flat-button
            color="primary"
            type="button"
            class="tc-steps__empty-cta"
            [disabled]="busy() || readOnly()"
            (click)="startDraft()"
          >
            {{ 'testCases.form.testSteps.addFirstStep' | translate }}
          </button>
        </div>
      }
    </div>
  }
</section>
